import React from 'react';

/**
 * Complete Bhava Bala Display Component
 * Shows strength analysis for all 12 houses (0-200% scale)
 */
const BhavaBalaComplete = ({ data }) => {
  if (!data || Object.keys(data).length === 0) {
    return <div className="p-4 bg-gray-50 rounded">No Bhava Bala data available</div>;
  }

  const getStrengthColor = (percentage) => {
    if (percentage >= 150) return 'text-green-600 font-bold';
    if (percentage >= 100) return 'text-blue-600 font-semibold';
    if (percentage >= 75) return 'text-yellow-600';
    if (percentage >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCategoryBadge = (category) => {
    const colors = {
      'Very Strong': 'bg-green-100 text-green-800 border-green-300',
      'Strong': 'bg-blue-100 text-blue-800 border-blue-300',
      'Moderate': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Weak': 'bg-orange-100 text-orange-800 border-orange-300',
      'Very Weak': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getHouseIcon = (house) => {
    const icons = {
      1: '🧘', 2: '💰', 3: '👥', 4: '🏠',
      5: '👶', 6: '⚔️', 7: '💑', 8: '🔮',
      9: '📚', 10: '👔', 11: '🎁', 12: '🌙'
    };
    return icons[house] || '🏛️';
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border-2 border-indigo-200">
        <h2 className="text-2xl font-bold text-indigo-900 mb-2">
          🏛️ Complete Bhava Bala (House Strength)
        </h2>
        <p className="text-sm text-gray-700 mb-2">
          Comprehensive strength analysis for all 12 houses (0-200% scale).
        </p>
        <div className="text-xs text-gray-600 space-y-1">
          <p>• <strong>Bhavadhipati Bala:</strong> Strength of house lord</p>
          <p>• <strong>Dig Bala:</strong> Directional strength (Kendra > Panapara > Apoklima)</p>
          <p>• <strong>Drishti Bala:</strong> Aspectual strength (benefic vs malefic)</p>
          <p>• <strong>Occupant Strength:</strong> Planets occupying the house</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(houseNum => {
          const houseData = data[houseNum];
          if (!houseData) return null;

          const sign = houseData.house_sign || '';
          const lord = houseData.house_lord || '';
          const strength = houseData.strength_percentage || 0;
          const category = houseData.strength_category || '';
          const signification = houseData.signification || '';
          const components = houseData.components || {};

          return (
            <div key={houseNum} className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              {/* Header */}
              <div className={`p-4 ${
                strength >= 150 ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                strength >= 100 ? 'bg-gradient-to-r from-blue-500 to-cyan-600' :
                strength >= 75 ? 'bg-gradient-to-r from-yellow-500 to-amber-600' :
                'bg-gradient-to-r from-orange-500 to-red-600'
              }`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">{getHouseIcon(houseNum)}</span>
                      <div>
                        <h3 className="text-xl font-bold text-white">House {houseNum}</h3>
                        <p className="text-sm text-white text-opacity-90">
                          {sign} • Lord: {lord}
                        </p>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getCategoryBadge(category)}`}>
                    {category}
                  </span>
                </div>
              </div>

              {/* Strength Indicator */}
              <div className="p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Total Strength</span>
                  <span className={`text-2xl font-bold ${getStrengthColor(strength)}`}>
                    {strength.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      strength >= 150 ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                      strength >= 100 ? 'bg-gradient-to-r from-blue-500 to-cyan-600' :
                      strength >= 75 ? 'bg-gradient-to-r from-yellow-500 to-amber-600' :
                      'bg-gradient-to-r from-orange-500 to-red-600'
                    }`}
                    style={{ width: `${Math.min(strength / 2, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Signification */}
              <div className="px-4 pt-3 pb-2">
                <p className="text-xs text-gray-600 italic">{signification}</p>
              </div>

              {/* Components Breakdown */}
              <div className="px-4 pb-4">
                <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Component Breakdown
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Bhavadhipati (Lord)</span>
                    <span className="font-semibold text-purple-600">
                      {components.bhavadhipati_bala?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Dig Bala (Direction)</span>
                    <span className="font-semibold text-blue-600">
                      {components.bhava_dig_bala?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Drishti (Aspects)</span>
                    <span className="font-semibold text-green-600">
                      {components.bhava_drishti_bala?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Occupant Strength</span>
                    <span className="font-semibold text-orange-600">
                      {components.occupant_strength?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Interpretation hint */}
              <div className={`px-4 py-2 text-xs ${
                strength >= 100 ? 'bg-green-50 text-green-700' :
                strength >= 75 ? 'bg-yellow-50 text-yellow-700' :
                'bg-red-50 text-red-700'
              }`}>
                {strength >= 100 ? '✅ Strong house - favorable results expected' :
                 strength >= 75 ? '⚠️ Moderate house - mixed results' :
                 '⚠️ Weak house - challenges possible, remedies recommended'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BhavaBalaComplete;
